import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Share, Plus, X, BellRing, Home, ChevronDown } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { getPushContext } from "@/lib/device";

const KEY = "rc_ios_install_prompt_at";
const COOLDOWN = 3 * 24 * 60 * 60 * 1000; // re-show at most every 3 days
const REVEAL_DELAY = 4000; // let the app settle before gently nudging

// iOS-only onboarding: web push requires the site be installed to the Home Screen and opened
// from that icon. Shown only for the "ios-pwa-not-installed" context; never on Android/desktop
// and never inside the Median native shell (getPushContext handles both).
export default function IOSInstallPrompt() {
  const { lang } = useI18n();
  const fr = lang === "fr";
  const [show, setShow] = useState(false);
  const [modal, setModal] = useState(false);

  useEffect(() => {
    if (getPushContext() !== "ios-pwa-not-installed") return;
    const last = Number(localStorage.getItem(KEY) || 0);
    if (Date.now() - last > COOLDOWN) {
      const timer = setTimeout(() => setShow(true), REVEAL_DELAY);
      return () => clearTimeout(timer);
    }
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(KEY, String(Date.now())); } catch (e) {}
    setShow(false); setModal(false);
  };

  const steps = fr
    ? ["Appuyez sur le bouton Partager", "Choisissez « Sur l'écran d'accueil »", "Ouvrez RoyalChat Nova depuis l'icône ajoutée"]
    : ["Tap the Share button", "Choose \"Add to Home Screen\"", "Open RoyalChat Nova from the new Home Screen icon"];

  return (
    <>
      <AnimatePresence>
        {show && !modal && (
          <motion.div
            initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 60 }}
            transition={{ type: "spring", stiffness: 260, damping: 26 }}
            data-testid="ios-install-banner"
            className="fixed inset-x-3 bottom-[calc(5rem+env(safe-area-inset-bottom))] z-[70]">
            <div className="relative overflow-hidden rounded-2xl border border-[var(--rc-gold)]/30 bg-[var(--rc-surface)]/95 backdrop-blur-xl shadow-[0_18px_50px_-12px_rgba(212,175,55,0.4)]">
              <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 12% 0%, rgba(212,175,55,0.12), transparent 55%)" }} />
              <div className="relative flex items-center gap-3 p-3.5">
                <motion.span
                  animate={{ scale: [1, 1.12, 1] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                  className="shrink-0 h-11 w-11 grid place-items-center rounded-full bg-[var(--rc-gold)]/12 border border-[var(--rc-gold)]/25 text-[var(--rc-gold)]">
                  <BellRing size={20} />
                </motion.span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-[var(--rc-text)] leading-tight">
                    {fr ? "Ne manquez aucun message" : "Never miss a message"}
                  </p>
                  <p className="text-xs text-[var(--rc-muted)] leading-snug mt-0.5">
                    {fr ? "Ajoutez RoyalChat Nova à l'écran d'accueil pour activer les notifications." : "Add RoyalChat Nova to your Home Screen to turn on notifications."}
                  </p>
                </div>
                <button onClick={dismiss} data-testid="ios-install-banner-dismiss"
                  className="shrink-0 h-7 w-7 grid place-items-center rounded-full text-[var(--rc-muted)] hover:bg-[var(--rc-bg)] transition-colors" aria-label="Dismiss"><X size={16} /></button>
              </div>
              <button onClick={() => setModal(true)} data-testid="ios-install-banner-open"
                className="relative w-full flex items-center justify-center gap-2 border-t border-[var(--rc-gold)]/20 bg-[var(--rc-gold)]/10 py-2.5 text-sm font-semibold text-[var(--rc-gold)] hover:bg-[var(--rc-gold)]/15 transition-colors">
                <Share size={15} /> {fr ? "Montrez-moi comment" : "Show me how"}
              </button>
            </div>
            {/* gentle hint toward Safari's bottom share bar */}
            <motion.div
              animate={{ y: [0, 6, 0], opacity: [0.55, 1, 0.55] }} transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
              className="mx-auto mt-1 w-max text-[var(--rc-gold)]/70" aria-hidden>
              <ChevronDown size={22} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {modal && (
          <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="ios-install-modal">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setModal(false)} />
            <motion.div initial={{ opacity: 0, scale: 0.94, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96 }} transition={{ duration: 0.22 }}
              className="relative w-full max-w-sm rounded-3xl border border-[var(--rc-gold)]/25 bg-[var(--rc-bg)] p-6">
              <button onClick={() => setModal(false)} data-testid="ios-install-modal-close"
                className="absolute top-4 right-4 h-9 w-9 grid place-items-center rounded-full text-[var(--rc-muted)] hover:bg-[var(--rc-surface)] transition-colors"><X size={18} /></button>
              <span className="h-14 w-14 grid place-items-center rounded-2xl bg-[var(--rc-gold)]/10 border border-[var(--rc-gold)]/20 text-[var(--rc-gold)] mb-4"><Home size={26} /></span>
              <h3 className="font-heading text-2xl mb-1">{fr ? "Ajouter à l'écran d'accueil" : "Add to Home Screen"}</h3>
              <p className="text-sm text-[var(--rc-muted)] mb-5">
                {fr ? "Les notifications sur iPhone nécessitent d'installer RoyalChat Nova depuis Safari." : "Notifications on iPhone require installing RoyalChat Nova from Safari."}
              </p>
              <ol className="space-y-3">
                {steps.map((s, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-[var(--rc-muted)]">
                    <span className="shrink-0 h-7 w-7 grid place-items-center rounded-full bg-[var(--rc-gold)]/15 text-[var(--rc-gold)] text-sm font-semibold">{i + 1}</span>
                    <span className="flex items-center gap-1.5 pt-0.5">
                      {i === 0 && <Share size={15} className="inline text-[var(--rc-gold)]" />}
                      {i === 1 && <Plus size={15} className="inline text-[var(--rc-gold)]" />}
                      {s}
                    </span>
                  </li>
                ))}
              </ol>
              <button onClick={dismiss} data-testid="ios-install-modal-dismiss"
                className="w-full mt-6 text-xs text-[var(--rc-muted)] hover:text-[var(--rc-text)] transition-colors">
                {fr ? "Ne plus afficher pour l'instant" : "Don't show this for now"}
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
